import { useState, useCallback } from 'react';
import { pdf } from '@react-pdf/renderer';
import { AlertMessage } from './useAlert';
import { logger } from '../utils/logger';

interface UsePdfGenerationProps {
  onError?: (error: AlertMessage) => void;
}

export const usePdfGeneration = ({ onError }: UsePdfGenerationProps = {}) => {
  const [pdfLoading, setPdfLoading] = useState(false);

  const generatePdf = useCallback(async (
    pdfComponent: React.ReactElement,
    filename: string
  ): Promise<boolean> => {
    try {
      setPdfLoading(true);
      
      const blob = await pdf(pdfComponent).toBlob();
      
      // Chrome-specific fix: Chrome requires the blob to have a proper MIME type
      // Create a new blob with explicit PDF MIME type
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      // Ensure filename has .pdf extension; Chrome may ignore missing extension
      link.download = /\.pdf$/i.test(filename) ? filename : `${filename}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);

      // Important for Chrome: trigger the click synchronously within the user gesture
      // Using setTimeout would break the user-activation chain and can cause Chrome to ignore the download filename
      link.click();

      // Cleanup after a short delay to ensure the download has started
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 500);
      
      return true;
    } catch (error) {
      logger.error('PDF Generation Error:', error);
      
      if (onError) {
        onError({
          type: 'error',
          message: 'PDF generation failed. This may be due to an image compatibility issue.'
        });
      }
      
      return false;
    } finally {
      setPdfLoading(false);
    }
  }, [onError]);

  return {
    pdfLoading,
    generatePdf
  };
};